import {
  AfterViewInit,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AbstractControl, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { currentUser } from '../../../../../core/auth';
import { AppState } from '../../../../../core/reducers';
import { Dictionary, Synonym } from '../../../concepts-management/models';
import { DictionaryService, SynonymService } from '../../../concepts-management/services';
import { Form, FieldSynonymLink } from '../../models';
import { LookupType } from '../../enums';
import { FormUtility, SchemaConfigs } from '../../utils';
import { FormService } from '../../services';
import { GeneralAutocompleteComponent } from '../../../../partials/layout/general-autocomplete/general-autocomplete.component';

function synonymRequired(control: AbstractControl): { [key: string]: boolean } | null {
  const synonym = control.value?.link?.synonym;

  return synonym === null || synonym === undefined || synonym === ''
    ? { synonymRequired: true }
    : null;
}

@Component({
  selector: 'kt-create-form-synonym-link',
  templateUrl: './create-form-synonym-link.component.html',
})
export class CreateFormSynonymLinkComponent implements AfterViewInit, OnDestroy, OnInit {
  subscriptions = new Subscription();
  lookupTypes: LookupType[] = Object.values(LookupType);
  links: FieldSynonymLink[] = [];
  companyId: number;
  formGroup: FormGroup;
  getDictionaryAutocomplete;
  getSynonymAutocomplete;
  @ViewChildren('synonymAutocomplete')
  private synonymAutocompletes: QueryList<GeneralAutocompleteComponent>;

  constructor(
    @Inject(MAT_DIALOG_DATA) public form: Form,
    private dialogRef: MatDialogRef<CreateFormSynonymLinkComponent>,
    private store: Store<AppState>,
    private formBuilder: FormBuilder,
    private dictionaryService: DictionaryService,
    private synonymService: SynonymService,
    private formService: FormService,
    private formUtility: FormUtility,
  ) {}

  ngOnInit(): void {
    this.formGroup = this.formBuilder.group({});

    this.subscriptions.add(
      this.store.select(currentUser).subscribe((user) => {
        this.companyId = user.companyID || 0;
        this.getDictionaryAutocomplete = this.dictionaryService.findDictionariesAutocomplete.bind(
          this.dictionaryService,
          this.companyId,
        );
      }),
    );

    this.subscriptions.add(
      this.formService.getFormSynonymLinks(this.form).subscribe(({ data: { links } }) => {
        if (!links?.length) return;

        this.links = links;
        const form = { ...this.form };
        const formSchema = JSON.parse(form.schema);
        const flattenedSchemaComponents = this.formUtility.getFlattenedSchemaComponents(formSchema);

        links.forEach((link) => {
          const fieldSchema = flattenedSchemaComponents?.find((c) => c.key === link.field_name);

          this.formGroup.addControl(
            link.field_name,
            new FormControl(
              {
                link,
                lookup: fieldSchema?.lookup || '',
                linkedTo: fieldSchema?.linkedTo || '',
                isSelect: fieldSchema?.type === 'select',
              },
              synonymRequired,
            ),
          );
        });
      }),
    );
  }

  ngAfterViewInit(): void {
    this.subscriptions.add(
      this.synonymAutocompletes.changes.subscribe((synonymAutos) =>
        synonymAutos.toArray().forEach((synonymAuto) => {
          const link = this.formGroup.controls[synonymAuto.id].value?.link;

          synonymAuto.setValue(link?.synonym);
          synonymAuto.updateValueAndValidity();
        }),
      ),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  handleDictionaryChange(dictionary: Dictionary): void {
    this.getSynonymAutocomplete = this.synonymService.findSynonymsAutocomplete.bind(
      this.synonymService,
      dictionary?.id,
    );
  }

  handleSynonymChange(fieldName: string, synonym: Synonym): void {
    const field = this.formGroup.controls[fieldName].value;

    if (!synonym || synonym.has_values) field.lookup = '';
    field.link.synonym = synonym;

    this.formGroup.controls[fieldName].patchValue(field);
  }

  formatDictionaryOption(dictionary: Dictionary): string {
    return dictionary?.name || '';
  }

  formatSynonymOption(synonym: Synonym): string {
    return synonym ? `${synonym.code} ${synonym.description}` : '';
  }

  formatSynonymValue(synonym: Synonym): string {
    return synonym ? `${synonym.code} (${synonym.dictionary?.name})` : '';
  }

  handleLookupChange(fieldName: string, lookup: LookupType | '') {
    const field = this.formGroup.controls[fieldName].value;

    field.lookup = lookup;
    this.formGroup.controls[fieldName].patchValue(field);
  }

  closeDialog(event: Event): void {
    event.stopPropagation();

    this.dialogRef.close();
  }

  submit(event: Event): void {
    event.stopPropagation();

    this.formGroup.markAllAsTouched();

    if (this.formGroup.invalid) return;

    const links = [];
    const form = { ...this.form };
    const formSchema = JSON.parse(form.schema);
    const flattenedSchemaComponents = this.formUtility.getFlattenedSchemaComponents(formSchema);
    const unlinkedComponents = flattenedSchemaComponents?.filter((c) => !c.linkedTo);

    if (!unlinkedComponents?.length) return;

    unlinkedComponents.forEach((c, idx) => {
      const field = this.formGroup.controls[c.key]?.value;

      if (field) {
        links.push(field.link);

        if (field.isSelect) {
          const schemaConfigs: SchemaConfigs = {
            companyId: this.companyId,
            lookupType: field.lookup,
            label: c.label,
            key: c.key,
            dataValues: c.data?.values?.length ? c.data.values : [],
            linkedTo: '',
          };

          unlinkedComponents[idx] = this.formUtility.getSchema(schemaConfigs);
        }
      }
    });

    const newFormSchema = this.formUtility.updateSchema(
      this.companyId,
      formSchema,
      unlinkedComponents,
    );

    form.schema = JSON.stringify(newFormSchema);

    this.formService
      .updateForm(form)
      .pipe(
        take(1),
        switchMap((res) => {
          if (!res.ok) return;

          return this.formService.updateFormSynonymLinks(form, links).pipe(take(1));
        }),
      )
      .subscribe((res) => {
        if (!res.ok) return;

        this.dialogRef.close(true);
      });
  }
}
